import { toast, ToastOptions, Id } from 'react-toastify';

// Define notification priority levels
export enum NotificationPriority {
  DEBUG = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3
}

// Default toast options by priority level
const defaultToastOptions: Record<NotificationPriority, ToastOptions> = {
  [NotificationPriority.DEBUG]: {
    position: 'bottom-right',
    autoClose: 2000,
    hideProgressBar: true,
  },
  [NotificationPriority.LOW]: {
    position: 'bottom-right',
    autoClose: 3000,
    hideProgressBar: false,
  },
  [NotificationPriority.MEDIUM]: {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
  },
  [NotificationPriority.HIGH]: {
    position: 'top-center',
    autoClose: 5000,
    hideProgressBar: false,
  },
};

// Current minimum priority level to display
// This can be configured based on environment or user preferences
// Default: show everything except DEBUG in production, show everything in development
let minimumPriorityLevel = process.env.NODE_ENV === 'production' 
  ? NotificationPriority.LOW 
  : NotificationPriority.DEBUG;

/**
 * Set the minimum notification priority level
 * @param level - The minimum priority level to display
 */
export const setMinimumPriorityLevel = (level: NotificationPriority): void => {
  minimumPriorityLevel = level;
};

/**
 * Get the current minimum notification priority level
 * @returns The current minimum priority level
 */
export const getMinimumPriorityLevel = (): NotificationPriority => {
  return minimumPriorityLevel;
};

/**
 * Enhanced notification function with priority filtering
 * @param content - Toast content (string or React element)
 * @param priority - Notification priority level
 * @param options - Standard toast options (optional)
 * @returns Toast ID if shown, undefined if suppressed
 */
export const notify = (
  content: React.ReactNode,
  priority: NotificationPriority,
  options?: ToastOptions
): Id | undefined => {
  // Skip notifications below the minimum priority level
  if (priority < minimumPriorityLevel) {
    return undefined;
  }

  // Merge default options for this priority with custom options
  const mergedOptions = {
    ...defaultToastOptions[priority],
    ...options,
  };

  return toast(content, mergedOptions);
};

/**
 * Show a success notification with priority
 */
export const notifySuccess = (
  content: React.ReactNode,
  priority: NotificationPriority = NotificationPriority.MEDIUM,
  options?: ToastOptions
): Id | undefined => {
  if (priority < minimumPriorityLevel) {
    return undefined;
  }
  
  const mergedOptions = {
    ...defaultToastOptions[priority],
    ...options,
  };
  
  return toast.success(content, mergedOptions);
};

/**
 * Show an error notification (always HIGH priority by default)
 */
export const notifyError = (
  content: React.ReactNode,
  priority: NotificationPriority = NotificationPriority.HIGH,
  options?: ToastOptions
): Id | undefined => {
  if (priority < minimumPriorityLevel) {
    return undefined;
  }
  
  const mergedOptions = {
    ...defaultToastOptions[priority],
    ...options,
  };
  
  return toast.error(content, mergedOptions);
};

/**
 * Show an info notification with priority
 */
export const notifyInfo = (
  content: React.ReactNode,
  priority: NotificationPriority = NotificationPriority.LOW,
  options?: ToastOptions
): Id | undefined => {
  if (priority < minimumPriorityLevel) {
    return undefined;
  }
  
  const mergedOptions = {
    ...defaultToastOptions[priority],
    ...options,
  };
  
  return toast.info(content, mergedOptions);
};

/**
 * Show a warning notification with priority
 */
export const notifyWarning = (
  content: React.ReactNode,
  priority: NotificationPriority = NotificationPriority.MEDIUM,
  options?: ToastOptions
): Id | undefined => {
  if (priority < minimumPriorityLevel) {
    return undefined;
  }
  
  const mergedOptions = {
    ...defaultToastOptions[priority],
    ...options,
  };
  
  return toast.warning(content, mergedOptions);
};

// Expose original toast functions for backward compatibility
export { toast };